import React, { memo, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { intervalToDatePickerValue, useDateLocale } from '../common/utils/utils';
import {
	ChangeSource,
	type DatePickerValue,
	type FuzzyDatePickerProps,
	type IntervalOption,
} from '../types';

import { DateRange } from './date-range';
import DateRangePickerContent from './date-range-picker-content';
import { messages } from './messages';

const styles = cssMap({
	confirmationMessage: {
		paddingInline: token('space.250'),
		paddingBlockEnd: token('space.250'),
	},
	confirmButtons: {
		paddingBlockEnd: token('space.250'),
		paddingInline: token('space.250'),
	},
});

export const DateRangePicker: React.MemoExoticComponent<
	(props: FuzzyDatePickerProps) => React.JSX.Element
> = memo((props: FuzzyDatePickerProps): React.JSX.Element => {
	const {
		value,
		defaultValue,
		onChange,
		placeholder,
		onClose,
		withTimeScale = true,
		appearance = 'default',
		disabled = false,
		minDate,
		maxDate,
		granularities,
		intervals,
		showPlaceholderWithSubtleText,
		intervalValue,
		selectIconPlacement = 'end',
		size,
		isRangeSelect,
		confirmationMessage,
		displayConfirmationButtons = false,
	} = props;

	const { formatMessage } = useIntl();
	const isControlled = 'value' in props;

	const isBundledUpdatesEnabled = fg('goal_types_m3_bundled_updates');

	const [isOpen, setIsOpen] = React.useState(false);
	const [currentValue, setCurentValue] = React.useState<DatePickerValue | undefined>(
		value ?? defaultValue,
	);
	const [selectedInterval, setSelectedInterval] = React.useState<IntervalOption | undefined>(
		intervalValue,
	);
	const [pendingValue, setPendingValue] = React.useState<
		{ value: DatePickerValue; confidence: string; interval?: IntervalOption } | undefined
	>(undefined);
	const locale = useDateLocale();

	React.useEffect(() => {
		if (isControlled && fg('projects_ai_updates')) {
			setCurentValue(value);
		}
	}, [value, isControlled]);

	React.useEffect(() => {
		if (fg('mercury_web_enable_intervals')) {
			if (intervalValue) {
				setCurentValue(
					intervalToDatePickerValue(intervalValue, intervalValue.startDate.getFullYear(), locale),
				);
			}
			setSelectedInterval(intervalValue);
		}
	}, [intervalValue, locale]);

	const togglePopup = useCallback(() => {
		if (!disabled) {
			if (isOpen) {
				onClose?.();
			}
			setIsOpen(!isOpen);
		}
	}, [isOpen, onClose, disabled]);

	const closePopup = useCallback(() => {
		setIsOpen(false);
		setPendingValue(undefined);
		onClose?.();
	}, [onClose]);

	const onDateChange = useCallback(
		(
			newValue?: DatePickerValue,
			confidence?: string,
			newSelectedInterval?: IntervalOption,
			source?: ChangeSource,
		) => {
			if (
				source === ChangeSource.SCALE_SELECT &&
				fg('mercury_web_daterangepicker_scale_changes_value')
			) {
				return;
			}

			if (isBundledUpdatesEnabled && displayConfirmationButtons && newValue && confidence) {
				setPendingValue({ value: newValue, confidence, interval: newSelectedInterval });
				return;
			}

			setCurentValue(newValue);
			setSelectedInterval(newValue !== undefined ? newSelectedInterval : undefined);
			onChange(newValue, confidence, newSelectedInterval);
			setIsOpen(false);
			onClose?.();
		},
		[onChange, onClose, displayConfirmationButtons, isBundledUpdatesEnabled],
	);

	const onConfirm = useCallback(() => {
		if (!pendingValue) {
			return;
		}
		setCurentValue(pendingValue.value);
		setSelectedInterval(pendingValue.interval);
		onChange(pendingValue.value, pendingValue.confidence, pendingValue.interval);
		setIsOpen(false);
		setPendingValue(undefined);
		onClose?.();
	}, [pendingValue, onChange, onClose]);

	const onClearDate = useCallback(() => {
		onDateChange();
	}, [onDateChange]);

	return (
		<Popup
			zIndex={610}
			placement="bottom-start"
			content={() => (
				<Stack>
					<DateRangePickerContent
						key={`${intervals?.length ?? 0}-${intervals?.[0]?.startDate.getTime() ?? ''}`}
						value={value}
						onChange={onDateChange}
						withTimeScale={withTimeScale}
						minDate={minDate}
						maxDate={maxDate}
						granularities={granularities}
						intervals={intervals}
						isRangeSelect={isRangeSelect}
					/>
					{isBundledUpdatesEnabled && displayConfirmationButtons && pendingValue && (
						<>
							{confirmationMessage && (
								<Box xcss={styles.confirmationMessage}>{confirmationMessage}</Box>
							)}
							<Box xcss={styles.confirmButtons}>
								<Inline space="space.100">
									<Button appearance="primary" onClick={onConfirm}>
										{formatMessage(messages.confirmButton)}
									</Button>
									<Button appearance="subtle" onClick={closePopup}>
										{formatMessage(messages.cancelButton)}
									</Button>
								</Inline>
							</Box>
						</>
					)}
				</Stack>
			)}
			isOpen={isOpen && !disabled}
			onClose={closePopup}
			shouldUseCaptureOnOutsideClick
			trigger={(triggerProps) => (
				<DateRange
					onClick={disabled ? undefined : togglePopup}
					value={currentValue}
					placeholder={placeholder}
					triggerProps={triggerProps}
					onClearDate={onClearDate}
					withTimeScale={withTimeScale}
					appearance={appearance}
					disabled={disabled}
					showPlaceholderWithSubtleText={showPlaceholderWithSubtleText}
					intervalDisplayName={selectedInterval?.name}
					selectIconPlacement={selectIconPlacement}
					size={size}
				/>
			)}
		/>
	);
});
